import React from "react";
import { getUIColorPalette } from "../../../theme/ColorPalette";
import { resolveRealtimeClockStyle } from "../../../theme/DelayStatusColors";
import { isTransitLikeTransportMode } from "../../../tools/TransportModeGroupsTools";
import { type ItineraryDTO, type ItineraryLegDTO } from "../../../types/dtos/api/itinerary/itinerary.dto";

interface ItineraryRealtimeClockProps {

    /** Observed ISO datetime shown on the clock (realtime when enrichment shifted it). */
    "timeIso": string | undefined;

    /** Planned ISO datetime. Falls back to a single live clock when omitted. */
    "scheduledTimeIso"?: string;

    /**
     * Optional timestamps used only for delay coloring when the displayed clock is not the
     * transit arrival itself (for example walk destination after a tram arrival).
     */
    "colorTimeIso"?: string;

    /** Planned ISO datetime paired with {@link colorTimeIso}. */
    "colorScheduledTimeIso"?: string;

    /**
     * Whether a realtime feed confirmed this clock.
     * Required to paint an on-time clock green; lateness still colors from the timestamps alone.
     */
    "hasRealtimeInfo": boolean;

    /** Whether the dark UI palette is active. */
    "isDarkUi": boolean;

    /**
     * Color used when status does not override the clock.
     * Omit to inherit the parent text color (list rows).
     */
    "neutralColor"?: string;

    /** Extra classes on the live time. */
    "className"?: string;

    /** Stacks the struck scheduled time under the live time, aligned to the end. */
    "alignEnd"?: boolean;
}

/**
 * Parses an ISO datetime into epoch milliseconds.
 *
 * @param iso - Candidate ISO string.
 * @returns Epoch milliseconds, or `null` when missing or invalid.
 */
const toEpochMs = ( iso: string | undefined ): number | null => {
    if ( iso === undefined || iso === "" ) {
        return null;
    }

    const parsed = new Date( iso ).getTime();

    return Number.isNaN( parsed ) ? null : parsed;
};

/**
 * Formats epoch milliseconds as a 24-hour `HH:mm` label.
 *
 * @param timeMs - Epoch milliseconds.
 * @returns Localized clock label.
 */
const formatClockLabel = ( timeMs: number ): string => {
    return new Date( timeMs ).toLocaleTimeString( [], {
        "hour":   "2-digit",
        "minute": "2-digit",
        "hour12": false,
    } );
};

/**
 * Finds the last public-transport leg in an itinerary (walking / bike after transit ignored).
 *
 * @param itinerary - Itinerary with ordered legs.
 * @returns Last transit-like leg, or `undefined` when none exist.
 */
export const findLastTransitLeg = ( itinerary: ItineraryDTO ): ItineraryLegDTO | undefined => {
    for ( let index = itinerary.legs.length - 1; index >= 0; index -= 1 ) {
        const leg = itinerary.legs[index];
        if ( leg !== undefined && isTransitLikeTransportMode( leg.mode ) ) {
            return leg;
        }
    }

    return undefined;
};

/**
 * Whether the itinerary's final destination step itself carries realtime confirmation.
 * Walk / bike endpoints after a transit arrival must stay neutral — color the transit stop instead.
 *
 * @param itinerary - Itinerary whose last leg may be a short walk to the destination.
 * @returns `true` only when the last leg is transit-like and marked realtime.
 */
export const itineraryDestinationHasConfirmedRealtime = ( itinerary: ItineraryDTO ): boolean => {
    const lastLeg = itinerary.legs[itinerary.legs.length - 1];
    if ( lastLeg === undefined || !isTransitLikeTransportMode( lastLeg.mode ) ) {
        return false;
    }

    return lastLeg.realTime === true;
};

/**
 * Realtime source for coloring a summary arrival clock (list / timing pill).
 * Uses the last transit leg's arrival, not a trailing walk destination.
 *
 * @param itinerary - Itinerary with optional realtime-enriched transit legs.
 * @returns Color timestamps and confirmation flag for the last transit arrival.
 */
export const resolveSummaryArrivalRealtimeSource = ( itinerary: ItineraryDTO ): {
    "colorTimeIso":          string | undefined;
    "colorScheduledTimeIso": string | undefined;
    "hasRealtimeInfo":       boolean;
} => {
    const lastTransitLeg = findLastTransitLeg( itinerary );

    return {
        "colorTimeIso":          lastTransitLeg?.to?.time,
        "colorScheduledTimeIso": lastTransitLeg?.to?.scheduledTime,
        "hasRealtimeInfo":       lastTransitLeg?.realTime === true,
    };
};

/**
 * Renders a realtime clock with the same colors as a departure:
 * green when confirmed on time, delay/advance colors from one minute of difference,
 * and no color change for a lateness under one minute.
 *
 * @param props - Clock timestamps and palette inputs.
 * @returns Live clock, optionally with the scheduled time struck through.
 */
const ItineraryRealtimeClock: React.FC<ItineraryRealtimeClockProps> = ( {
    timeIso,
    scheduledTimeIso,
    colorTimeIso,
    colorScheduledTimeIso,
    hasRealtimeInfo,
    isDarkUi,
    neutralColor,
    className = "text-xs font-semibold tabular-nums",
    alignEnd = false,
} ) => {
    const timeMs = toEpochMs( timeIso );
    if ( timeMs === null ) {
        return null;
    }

    const colorTimeMs = toEpochMs( colorTimeIso ) ?? timeMs;
    const colorScheduledMs = toEpochMs( colorScheduledTimeIso ?? scheduledTimeIso );
    const clockStyle = resolveRealtimeClockStyle( isDarkUi, colorTimeMs, colorScheduledMs, hasRealtimeInfo );
    const liveLabel = formatClockLabel( timeMs );
    const displayScheduledMs = toEpochMs( scheduledTimeIso );
    const scheduledLabel = displayScheduledMs === null ? "" : formatClockLabel( displayScheduledMs );
    const showScheduledStrike = clockStyle.showScheduledStrike &&
      scheduledLabel !== "" &&
      scheduledLabel !== liveLabel;
    const liveColor = clockStyle.textColor ?? neutralColor;
    const alignmentClassName = alignEnd ? "text-right" : "text-left";
    const endAlignedClassName = alignEnd ? "ml-auto" : "";

    if ( !showScheduledStrike ) {
        return (
            <span
                className={`${ endAlignedClassName } ${ className } ${ alignmentClassName }`}
                style={liveColor !== undefined ? { "color": liveColor } : undefined}
            >
                {liveLabel}
            </span>
        );
    }

    const palette = getUIColorPalette( isDarkUi );

    return (
        <span className={`${ endAlignedClassName } flex flex-col ${ alignEnd ? "items-end" : "items-start" }`}>
            <span
                className={`${ className } ${ alignmentClassName }`}
                style={liveColor !== undefined ? { "color": liveColor } : undefined}
            >
                {liveLabel}
            </span>
            <span
                className={`text-xs font-semibold tabular-nums line-through ${ alignmentClassName }`}
                style={{ "color": palette.overlayTextSecondary }}
            >
                {scheduledLabel}
            </span>
        </span>
    );
};

export default ItineraryRealtimeClock;
